import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FaSort, FaSearch, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import { useItems } from "../../hooks/useItems";
import Modal from "../../components/forms/Modal";
import ItemsForm from "../../components/forms/ItemsForm";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import { useIsMobile } from "../../hooks/useIsMobile";
import { normalizeItemSearchText } from "../../utils/itemSearchText";
import "./Items.css";

// Constants
const ITEM_STAT_FILTER_ALL = "all";
const ITEM_STAT_FILTER_CHECKED = "checked";
const ITEM_STAT_FILTER_UNCHECKED = "unchecked";
const ITEM_STAT_FILTER_WITH_NOTES = "withNotes";

const DEFAULT_ITEMS_PER_PAGE = 25;
const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const SEARCH_DEBOUNCE_DELAY = 300;

// Table columns configuration
const TABLE_COLUMNS = [
  { key: "itemName", label: "שם פריט" },
  { key: "isChecked", label: "צ'", noSort: true },
  { key: "makat", label: 'מק"ט' },
  { key: "note", label: "הערה" },
  { key: "actions", label: "פעילות", noSort: true },
];

// Helper functions
const createSearchText = (item) =>
  [
    normalizeItemSearchText(item.itemName),
    normalizeItemSearchText(item.note),
    normalizeItemSearchText(item.makat),
  ].join(" ");

const sortItems = (items, sortField, sortDirection) => {
  return items.sort((a, b) => {
    let aValue = a[sortField] ?? "";
    let bValue = b[sortField] ?? "";

    const aStr = normalizeItemSearchText(aValue);
    const bStr = normalizeItemSearchText(bValue);

    const result = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
    return sortDirection === "asc" ? result : -result;
  });
};

// Memoized table row component for better performance
const ItemRow = React.memo(
  ({ item, canManageItems, operationLoading, onEdit, onDelete }) => (
    <tr key={item.id || item.itemId}>
      <td>{item.itemName || "לא זמין"}</td>
      <td>
        <span
          className={`status-badge ${
            item.isChecked ? "status-checked" : "status-unchecked"
          }`}
        >
          {item.isChecked ? "כן" : "לא"}
        </span>
      </td>
      <td>{item.makat || ""}</td>
      <td>{item.note?.trim() ? item.note : ""}</td>
      <td className="actions">
        {canManageItems ? (
          <>
            <button
              className="edit-btn"
              onClick={() => onEdit(item)}
              title="ערוך"
              disabled={operationLoading}
            >
              <FaEdit />
            </button>
            <button
              className="delete-btn"
              onClick={() => onDelete(item.id || item.itemId)}
              title="מחק"
              disabled={operationLoading}
            >
              <FaTrash />
            </button>
          </>
        ) : (
          <span className="view-only">צפייה בלבד</span>
        )}
      </td>
    </tr>
  )
);

ItemRow.displayName = "ItemRow";

const ItemsMobileCard = React.memo(function ItemsMobileCard({
  item,
  canManageItems,
  operationLoading,
  onEdit,
  onDelete,
}) {
  const id = item.id || item.itemId;
  return (
    <div className="items-mobile-card">
      <div className="items-mobile-card-header">
        <span className="items-mobile-item-name">
          {item.itemName || "לא זמין"}
        </span>
      </div>
      <div className="items-mobile-card-body">
        <div className="items-mobile-field">
          <span className="items-mobile-label">צ&apos;</span>
          <span
            className={`status-badge ${
              item.isChecked ? "status-checked" : "status-unchecked"
            }`}
          >
            {item.isChecked ? "כן" : "לא"}
          </span>
        </div>
        {item.makat ? (
          <div className="items-mobile-field">
            <span className="items-mobile-label">מק&quot;ט</span>
            <span className="items-mobile-value">{item.makat}</span>
          </div>
        ) : null}
        {item.note?.trim() ? (
          <div className="items-mobile-field items-mobile-field-note">
            <span className="items-mobile-label">הערה</span>
            <span className="items-mobile-value">{item.note}</span>
          </div>
        ) : null}
      </div>
      <div className="items-mobile-card-actions">
        {canManageItems ? (
          <>
            <button
              type="button"
              className="items-mobile-action-btn edit-btn"
              onClick={() => onEdit(item)}
              disabled={operationLoading}
              title="ערוך"
            >
              <FaEdit /> ערוך
            </button>
            <button
              type="button"
              className="items-mobile-action-btn delete-btn"
              onClick={() => onDelete(id)}
              disabled={operationLoading}
              title="מחק"
            >
              <FaTrash /> מחק
            </button>
          </>
        ) : (
          <span className="view-only">צפייה בלבד</span>
        )}
      </div>
    </div>
  );
});

const Items = () => {
  const isMobile = useIsMobile();
  // Hooks
  const { user, isAdmin } = useAuth();
  const { items, loading, error, addItem, updateItem, deleteItem } = useItems();

  // Permissions
  const canManageItems = isAdmin;
  const canViewItems = isAdmin || user?.permissions === "parentsUser";
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState("");
  const [sortField, setSortField] = useState("itemName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [itemStatFilter, setItemStatFilter] = useState(ITEM_STAT_FILTER_ALL);

  // Debounced search optimization
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const itemsMatchingStat = useMemo(() => {
    if (!items.length) return [];
    switch (itemStatFilter) {
      case ITEM_STAT_FILTER_CHECKED:
        return items.filter((item) => item.isChecked);
      case ITEM_STAT_FILTER_UNCHECKED:
        return items.filter((item) => !item.isChecked);
      case ITEM_STAT_FILTER_WITH_NOTES:
        return items.filter((item) => item.note && item.note.trim());
      default:
        return items;
    }
  }, [items, itemStatFilter]);

  const filteredItems = useMemo(() => {
    if (!items.length) return [];

    let filtered = itemsMatchingStat;
    if (debouncedSearchTerm.trim()) {
      const lowerSearchTerm = normalizeItemSearchText(debouncedSearchTerm);
      filtered = itemsMatchingStat.filter((item) =>
        createSearchText(item).includes(lowerSearchTerm)
      );
    }

    return sortItems([...filtered], sortField, sortDirection);
  }, [items.length, itemsMatchingStat, debouncedSearchTerm, sortField, sortDirection]);

  const emptyListMessage = useMemo(() => {
    if (!items.length) return "אין פריטים במערכת";
    if (!itemsMatchingStat.length) {
      if (itemStatFilter === ITEM_STAT_FILTER_CHECKED) {
        return "אין פריטים מצפינים";
      }
      if (itemStatFilter === ITEM_STAT_FILTER_UNCHECKED) {
        return "אין פריטים ללא צ'";
      }
      if (itemStatFilter === ITEM_STAT_FILTER_WITH_NOTES) {
        return "אין פריטים עם הערות";
      }
      return "אין פריטים במערכת";
    }
    if (!filteredItems.length) {
      return "לא נמצאו פריטים התואמים לחיפוש";
    }
    return "";
  }, [
    items.length,
    itemsMatchingStat.length,
    filteredItems.length,
    itemStatFilter,
  ]);

  // Statistics calculation with memoization
  const statistics = useMemo(() => {
    const stats = {
      total: items.length,
      checked: items.filter((item) => item.isChecked).length,
      unchecked: items.filter((item) => !item.isChecked).length,
      withNotes: items.filter((item) => item.note && item.note.trim()).length,
    };
    return stats;
  }, [items]);

  // Pagination calculations with memoization
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    return {
      totalPages,
      startIndex,
      endIndex,
      paginatedItems,
      totalItems: filteredItems.length,
    };
  }, [filteredItems, currentPage, itemsPerPage]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, sortField, sortDirection, itemStatFilter]);

  // Event handlers
  const handleEdit = useCallback((item) => {
    setEditingItem(item);
  }, []);

  const handleAddItem = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");

      const result = await addItem(formData);
      if (result.success) {
        setShowAddModal(false);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [addItem]
  );

  const handleUpdateItem = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");

      const result = await updateItem(
        editingItem.id || editingItem.itemId,
        formData
      );
      if (result.success) {
        setEditingItem(null);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [updateItem, editingItem]
  );

  const handleDeleteItem = useCallback(
    async (itemId) => {
      if (
        window.confirm(
          "האם אתה בטוח שברצונך למחוק פריט זה?\n\nפעולה זו לא ניתנת לביטול!"
        )
      ) {
        setOperationLoading(true);
        setOperationError("");

        const result = await deleteItem(itemId);
        if (!result.success) {
          setOperationError(result.error);
        }
        setOperationLoading(false);
      }
    },
    [deleteItem]
  );

  const handleCloseModal = useCallback(() => {
    setEditingItem(null);
    setShowAddModal(false);
    setOperationError("");
  }, []);

  const handleSort = useCallback(
    (field) => {
      setSortField(field);
      setSortDirection((prev) =>
        sortField === field && prev === "asc" ? "desc" : "asc"
      );
    },
    [sortField]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const sortFieldOptions = useMemo(
    () => TABLE_COLUMNS.filter((c) => !c.noSort),
    []
  );

  // Early returns for better performance
  if (!canViewItems) {
    return (
      <div
        id="items-page-scope"
        className="items-management-container"
      >
        <div className="access-denied">
          <h2>🚫 אין לך הרשאה לגשת לעמוד זה</h2>
          <p>רק מנהלים ומשתמשי מחלקת קשר יכולים לצפות בפריטים</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        id="items-page-scope"
        className="items-management-container"
      >
        <LoadingSpinner message="טוען פריטים..." />
      </div>
    );
  }

  return (
    <div id="items-page-scope" className="items-management-container">
      {error && <ErrorMessage message={error} />}
      {operationError && (
        <ErrorMessage
          message={operationError}
          onDismiss={() => setOperationError("")}
        />
      )}

      <div className="items-management-header">
        <h1>הגדרות פריטים</h1>
        <div className="items-header-actions">
          {canManageItems && (
            <button
              className="add-item-btn"
              onClick={() => setShowAddModal(true)}
              disabled={operationLoading}
            >
              <FaPlus />
              הוסף פריט
            </button>
          )}
        </div>
      </div>

      <div className="search-container">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="חפש בפריטים..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Statistics — click to filter the list */}
      <div
        className="items-statistics-container"
        role="toolbar"
        aria-label="סינון לפי סוג פריט"
      >
        <button
          type="button"
          className={`items-stat-card-filter${itemStatFilter === ITEM_STAT_FILTER_ALL ? " items-stat-card-filter-active" : ""}`}
          onClick={() => setItemStatFilter(ITEM_STAT_FILTER_ALL)}
          aria-pressed={itemStatFilter === ITEM_STAT_FILTER_ALL}
        >
          <span className="stat-label">סה"כ פריטים</span>
          <span className="stat-value">{statistics.total}</span>
        </button>
        <button
          type="button"
          className={`items-stat-card-filter${itemStatFilter === ITEM_STAT_FILTER_CHECKED ? " items-stat-card-filter-active" : ""}`}
          onClick={() => setItemStatFilter(ITEM_STAT_FILTER_CHECKED)}
          aria-pressed={itemStatFilter === ITEM_STAT_FILTER_CHECKED}
        >
          <span className="stat-label">מצפינים</span>
          <span className="stat-value">{statistics.checked}</span>
        </button>
        <button
          type="button"
          className={`items-stat-card-filter${itemStatFilter === ITEM_STAT_FILTER_UNCHECKED ? " items-stat-card-filter-active" : ""}`}
          onClick={() => setItemStatFilter(ITEM_STAT_FILTER_UNCHECKED)}
          aria-pressed={itemStatFilter === ITEM_STAT_FILTER_UNCHECKED}
        >
          <span className="stat-label">ללא צ'</span>
          <span className="stat-value">{statistics.unchecked}</span>
        </button>
        <button
          type="button"
          className={`items-stat-card-filter${itemStatFilter === ITEM_STAT_FILTER_WITH_NOTES ? " items-stat-card-filter-active" : ""}`}
          onClick={() => setItemStatFilter(ITEM_STAT_FILTER_WITH_NOTES)}
          aria-pressed={itemStatFilter === ITEM_STAT_FILTER_WITH_NOTES}
        >
          <span className="stat-label">עם הערות</span>
          <span className="stat-value">{statistics.withNotes}</span>
        </button>
      </div>

      {isMobile && (
        <div className="items-mobile-sort">
          <label htmlFor="items-mobile-sort-field">מיון:</label>
          <select
            id="items-mobile-sort-field"
            value={sortField}
            onChange={(e) => handleSort(e.target.value)}
            className="items-mobile-sort-select"
          >
            {sortFieldOptions.map((col) => (
              <option key={col.key} value={col.key}>
                {col.label}{" "}
                {sortField === col.key
                  ? sortDirection === "asc"
                    ? "↑"
                    : "↓"
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {isMobile ? (
        paginationData.paginatedItems.length === 0 ? (
          <div className="items-mobile-empty">{emptyListMessage}</div>
        ) : (
          <div className="items-mobile-cards">
            {paginationData.paginatedItems.map((item) => (
              <ItemsMobileCard
                key={item.id || item.itemId}
                item={item}
                canManageItems={canManageItems}
                operationLoading={operationLoading}
                onEdit={handleEdit}
                onDelete={handleDeleteItem}
              />
            ))}
            <p className="items-mobile-count" aria-live="polite">
              {paginationData.totalPages > 1
                ? `סה"כ ${paginationData.totalItems} פריטים · עמוד ${currentPage} מתוך ${paginationData.totalPages}`
                : `סה"כ ${paginationData.totalItems} פריטים`}
            </p>
          </div>
        )
      ) : (
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    onClick={
                      column.noSort ? undefined : () => handleSort(column.key)
                    }
                    style={{ cursor: column.noSort ? "default" : "pointer" }}
                  >
                    {!column.noSort && <FaSort />}
                    {column.label}
                    {!column.noSort && sortField === column.key && (
                      <span style={{ marginRight: "5px" }}>
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginationData.paginatedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {emptyListMessage}
                  </td>
                </tr>
              ) : (
                paginationData.paginatedItems.map((item) => (
                  <ItemRow
                    key={item.id || item.itemId}
                    item={item}
                    canManageItems={canManageItems}
                    operationLoading={operationLoading}
                    onEdit={handleEdit}
                    onDelete={handleDeleteItem}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination (desktop + mobile) */}
      {paginationData.totalItems > itemsPerPage && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              מציג {paginationData.startIndex + 1}-
              {Math.min(paginationData.endIndex, paginationData.totalItems)}{" "}
              מתוך {paginationData.totalItems} פריטים
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="items-per-page-select"
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} פריטים בעמוד
                </option>
              ))}
            </select>
          </div>

          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ראשון
            </button>
            <button
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              הקודם
            </button>

            <span className="page-info">
              עמוד {currentPage} מתוך {paginationData.totalPages}
            </span>

            <button
              onClick={() =>
                handlePageChange(
                  Math.min(currentPage + 1, paginationData.totalPages)
                )
              }
              disabled={currentPage === paginationData.totalPages}
              className="pagination-btn"
            >
              הבא
            </button>
            <button
              onClick={() => handlePageChange(paginationData.totalPages)}
              disabled={currentPage === paginationData.totalPages}
              className="pagination-btn"
            >
              אחרון
            </button>
          </div>
        </div>
      )}

      {canManageItems && (
        <>
          <Modal
            isOpen={!!editingItem}
            onClose={handleCloseModal}
            title="עריכת פריט"
            className="items-settings-modal"
          >
            <ItemsForm
              initialValues={{
                itemName: editingItem?.itemName || "",
                isChecked: editingItem?.isChecked || false,
                makat: editingItem?.makat || "",
                note: editingItem?.note || "",
              }}
              onSubmit={handleUpdateItem}
              onCancel={handleCloseModal}
              submitText="שמור שינויים"
              isEditing={true}
            />
          </Modal>

          <Modal
            isOpen={showAddModal}
            onClose={handleCloseModal}
            title="הוספת פריט חדש"
            className="items-settings-modal items-settings-modal-add"
          >
            <ItemsForm
              initialValues={{
                itemName: "",
                isChecked: false,
                makat: "",
                note: "",
              }}
              onSubmit={handleAddItem}
              onCancel={handleCloseModal}
              submitText="הוסף פריט"
              isEditing={false}
            />
          </Modal>
        </>
      )}

    </div>
  );
};

export default Items;
